import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './AdminPanel.css';
import {
    Plus, Download,
    MoreHorizontal, ChevronLeft, ChevronRight, Trash2, X, Search
} from 'lucide-react';

import { db } from '../../firebase';
import { collection, query, orderBy, getDocs, deleteDoc, doc, writeBatch, addDoc, serverTimestamp } from 'firebase/firestore';

import SchedulesSection from './sections/SchedulesSection';
import UsersSection from './sections/UsersSection';
import SettingsSection from './sections/SettingsSection';
import EmailServerSection from './sections/EmailServerSection';
import ScriptsSection from './sections/ScriptsSection';

const AdminDashboard = () => {
    const location = useLocation();
    const [activePanel, setActivePanel] = useState(location.state?.activePanel || 'dashboard');

    useEffect(() => {
        if (location.state?.activePanel) {
            setActivePanel(location.state.activePanel);
        }
    }, [location.state]);
    const [leads, setLeads] = useState([]);
    const [leadSearch, setLeadSearch] = useState('');

    const [isLoading, setIsLoading] = useState(true);
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [activeMenuId, setActiveMenuId] = useState(null); // Track which row has open menu

    // Lead Modal State
    const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
    const [newLead, setNewLead] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        source: 'manual',
        country: ''
    });

    const handleAddLead = async (e) => {
        e.preventDefault();
        try {
            const docRef = await addDoc(collection(db, 'leads'), {
                ...newLead,
                date_created: serverTimestamp()
            });

            // Update local state
            const createdLead = {
                id: docRef.id,
                ...newLead,
                full_name: newLead.name, // Normalize for table
                company_name: newLead.company,
                phone_number: newLead.phone,
                date_submitted: new Date().toLocaleDateString()
            };

            setLeads([createdLead, ...leads]);
            setIsLeadModalOpen(false);
            setNewLead({ name: '', email: '', company: '', phone: '', source: 'manual', country: '' });
            alert("Lead added successfully!");
        } catch (error) {
            console.error("Error adding lead: ", error);
            alert("Failed to add lead.");
        }
    };

    useEffect(() => {
        const fetchLeads = async () => {
            try {
                setIsLoading(true);
                const q = query(collection(db, 'leads'), orderBy('date_created', 'desc'));
                const querySnapshot = await getDocs(q);
                const leadsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    // Normalize fields to match table structure
                    full_name: doc.data().name || 'Unknown',
                    company_name: doc.data().company || '-',
                    phone_number: doc.data().phone || '-',
                    date_submitted: doc.data().date_created?.toDate().toLocaleDateString() || new Date().toLocaleDateString(),
                    role: '-', // generic as we don't capture this yet
                    country: '-', // generic
                }));
                setLeads(leadsData);
            } catch (error) {
                console.error("Error fetching leads: ", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLeads();
    }, []);

    const filteredLeads = leads.filter(l =>
        l.full_name.toLowerCase().includes(leadSearch.toLowerCase()) ||
        l.company_name.toLowerCase().includes(leadSearch.toLowerCase()) ||
        l.email.toLowerCase().includes(leadSearch.toLowerCase())
    );

    // Toggle Select All
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedLeads(filteredLeads.map(l => l.id));
        } else {
            setSelectedLeads([]);
        }
    };

    // Toggle Single Selection
    const handleSelectLead = (id) => {
        setSelectedLeads(prev => {
            if (prev.includes(id)) {
                return prev.filter(leadId => leadId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // Toggle 3-dot menu
    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (activeMenuId === id) {
            setActiveMenuId(null);
        } else {
            setActiveMenuId(id);
        }
    };

    // Close menu when clicking elsewhere
    useEffect(() => {
        const handleClickOutside = () => setActiveMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Delete Single Lead
    const handleDeleteLead = async (id) => {
        if (window.confirm("Are you sure you want to permanently delete this lead?")) {
            try {
                await deleteDoc(doc(db, 'leads', id));
                // Update local state
                setLeads(prev => prev.filter(l => l.id !== id));
                setSelectedLeads(prev => prev.filter(leadId => leadId !== id));
            } catch (error) {
                console.error("Error deleting lead:", error);
                alert("Failed to delete lead.");
            }
        }
    };

    // Bulk Delete
    const handleBulkDelete = async () => {
        if (window.confirm(`Are you sure you want to delete ${selectedLeads.length} selected leads? This cannot be undone.`)) {
            try {
                const batch = writeBatch(db);
                selectedLeads.forEach(id => {
                    const ref = doc(db, 'leads', id);
                    batch.delete(ref);
                });
                await batch.commit();

                // Update local state
                setLeads(prev => prev.filter(l => !selectedLeads.includes(l.id)));
                setSelectedLeads([]);
            } catch (error) {
                console.error("Error deleting leads:", error);
                alert("Failed to delete selected leads.");
            }
        }
    };

    const getSourceBadgeClass = (src) => {
        switch (src) {
            case 'google': return 'badge-google';
            case 'meta': return 'badge-meta';
            case 'organic': return 'badge-organic';
            case 'bing': return 'badge-bing';
            default: return 'badge-other';
        }
    };

    const handleExportCSV = () => {
        if (leads.length === 0) {
            alert("No leads to export.");
            return;
        }

        const headers = ["ID", "Date", "Name", "Email", "Company", "Phone", "Helper Role", "Source", "Country"];
        const csvRows = [headers.join(',')];

        leads.forEach(lead => {
            const row = [
                `"${lead.id}"`,
                `"${lead.date_submitted}"`,
                `"${lead.full_name}"`,
                `"${lead.email}"`,
                `"${lead.company_name}"`,
                `"${lead.phone_number}"`,
                `"${lead.role}"`,
                `"${lead.source}"`,
                `"${lead.country}"`
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div className="panel active">
            {/* ── DASHBOARD PANEL ── */}
            {activePanel === 'dashboard' && (
                <div className="panel active">
                    <div className="page-hdr">
                        <div className="page-hdr-left">
                            <div className="eyebrow">Overview</div>
                            <h1>Dashboard <span>.</span></h1>
                        </div>
                    </div>
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="s-label">Total Leads</div>
                            <div className="s-val">{leads.length}</div>
                        </div>
                        <div className="stat-card green">
                            <div className="s-label">New Today</div>
                            <div className="s-val">12</div>
                        </div>
                        <div className="stat-card purple">
                            <div className="s-label">Active Users</div>
                            <div className="s-val">840</div>
                        </div>
                        <div className="stat-card warn">
                            <div className="s-label">Pending Calls</div>
                            <div className="s-val">5</div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── LEADS PANEL ── */}
            {activePanel === 'leads' && (
                <div className="panel active">
                    <div className="page-hdr">
                        <div className="page-hdr-left">
                            <div className="eyebrow">Database</div>
                            <h1>Leads <span>Records</span></h1>
                        </div>
                        <div className="page-hdr-actions">
                            {selectedLeads.length > 0 && (
                                <button className="btn btn-danger" onClick={handleBulkDelete} style={{ backgroundColor: '#ef4444', color: 'white', borderColor: '#ef4444', marginRight: '8px' }}>
                                    <Trash2 size={12} /> Delete Selected ({selectedLeads.length})
                                </button>
                            )}
                            <button className="btn btn-ghost" onClick={handleExportCSV}><Download size={12} /> Export CSV</button>
                            <button className="btn btn-primary" onClick={() => setIsLeadModalOpen(true)}><Plus size={12} /> Add Lead</button>
                        </div>
                    </div>

                    <div className="stats-grid">
                        <div className="stat-card"><div className="s-label">Total</div><div className="s-val">{leads.length}</div></div>
                        <div className="stat-card green"><div className="s-label">Organic</div><div className="s-val">{leads.filter(l => l.source === 'organic').length}</div></div>
                        <div className="stat-card purple"><div className="s-label">Google Ads</div><div className="s-val">{leads.filter(l => l.source === 'google').length}</div></div>
                        <div className="stat-card warn"><div className="s-label">Meta Ads</div><div className="s-val">{leads.filter(l => l.source === 'meta').length}</div></div>
                    </div>

                    <div className="toolbar">
                        <div className="search-wrap">
                            <Search size={14} />
                            <input
                                className="search-input"
                                type="text"
                                placeholder="Name, email, company..."
                                value={leadSearch}
                                onChange={(e) => setLeadSearch(e.target.value)}
                            />
                        </div>
                        <select className="filter-select">
                            <option value="">All Sources</option>
                            <option value="google">Google</option>
                            <option value="meta">Meta</option>
                            <option value="organic">Organic</option>
                        </select>
                    </div>

                    <div className="tbl-wrap">
                        <div className="tbl-scroll">
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>
                                            <input
                                                type="checkbox"
                                                onChange={handleSelectAll}
                                                checked={filteredLeads.length > 0 && selectedLeads.length === filteredLeads.length}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </th>
                                        <th>Date</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Company</th>
                                        <th>Role</th>
                                        <th>Source</th>
                                        <th>Country</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan="9" style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>Loading leads...</td>
                                        </tr>
                                    ) : filteredLeads.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>No leads found.</td>
                                        </tr>
                                    ) : filteredLeads.map(lead => (
                                        <tr key={lead.id} style={selectedLeads.includes(lead.id) ? { backgroundColor: 'rgba(162, 203, 82, 0.1)' } : {}}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeads.includes(lead.id)}
                                                    onChange={() => handleSelectLead(lead.id)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </td>
                                            <td className="muted">{lead.date_submitted}</td>
                                            <td style={{ fontWeight: 500 }}>{lead.full_name}</td>
                                            <td>{lead.email}</td>
                                            <td>{lead.company_name}</td>
                                            <td className="muted">{lead.role}</td>
                                            <td><span className={`badge ${getSourceBadgeClass(lead.source)}`}>{lead.source}</span></td>
                                            <td>{lead.country}</td>
                                            <td>
                                                <div className="action-btns" style={{ opacity: 1, position: 'relative' }}>
                                                    <button
                                                        className="btn-ghost"
                                                        style={{ padding: '4px', height: 'auto' }}
                                                        onClick={(e) => toggleMenu(lead.id, e)}
                                                    >
                                                        <MoreHorizontal size={14} />
                                                    </button>
                                                    {activeMenuId === lead.id && (
                                                        <div className="dropdown-menu" style={{
                                                            position: 'absolute',
                                                            right: '0',
                                                            top: '100%',
                                                            background: 'white',
                                                            border: '1px solid #eee',
                                                            borderRadius: '4px',
                                                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                                            zIndex: 10,
                                                            minWidth: '120px'
                                                        }}>
                                                            <button
                                                                onClick={() => handleDeleteLead(lead.id)}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    padding: '8px 12px',
                                                                    width: '100%',
                                                                    textAlign: 'left',
                                                                    color: '#ef4444',
                                                                    fontSize: '13px',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                            >
                                                                <Trash2 size={14} /> Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="pagination">
                            <div className="info">Showing {filteredLeads.length} leads</div>
                            <div className="pages">
                                <button className="page-btn"><ChevronLeft size={12} /></button>
                                <button className="page-btn active">1</button>
                                <button className="page-btn">2</button>
                                <button className="page-btn">3</button>
                                <button className="page-btn"><ChevronRight size={12} /></button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── PLACEHOLDER PANELS ── */}
            {activePanel === 'schedules' && <SchedulesSection />}
            {activePanel === 'users' && <UsersSection />}
            {activePanel === 'settings' && <SettingsSection />}
            {activePanel === 'email-server' && <EmailServerSection />}
            {activePanel === 'scripts' && <ScriptsSection />}

            {/* ── CREATE LEAD MODAL ── */}
            {isLeadModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-[500px] shadow-xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold">Add New Lead</h3>
                            <button onClick={() => setIsLeadModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleAddLead} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                    <input required type="text" className="w-full border rounded px-3 py-2" value={newLead.name} onChange={e => setNewLead({ ...newLead, name: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input required type="email" className="w-full border rounded px-3 py-2" value={newLead.email} onChange={e => setNewLead({ ...newLead, email: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                                    <input type="text" className="w-full border rounded px-3 py-2" value={newLead.company} onChange={e => setNewLead({ ...newLead, company: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <input type="text" className="w-full border rounded px-3 py-2" value={newLead.phone} onChange={e => setNewLead({ ...newLead, phone: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                    <input type="text" className="w-full border rounded px-3 py-2" value={newLead.country} onChange={e => setNewLead({ ...newLead, country: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                                    <select className="w-full border rounded px-3 py-2" value={newLead.source} onChange={e => setNewLead({ ...newLead, source: e.target.value })}>
                                        <option value="manual">Manual Entry</option>
                                        <option value="phone">Phone Inquiry</option>
                                        <option value="email">Email Inquiry</option>
                                        <option value="referral">Referral</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button type="button" onClick={() => setIsLeadModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-[#A2CB52] text-white rounded hover:bg-[#8bae46]">Create Lead</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
